import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * Resolve a topic from URL segments in ONE round-trip.
 *
 * Old behavior: cascading category lookups (1→2→3) then topic then profile
 * then posts count = up to 6 sequential queries. New behavior: query topic by
 * slug with nested categories embedded, then validate the hierarchy in JS.
 * reply_count from the topic row is used instead of a separate posts count query.
 */
export const useHierarchicalTopic = (slugSegments: string[]) => {
  return useQuery({
    queryKey: ['hierarchical-topic', ...slugSegments],
    queryFn: async (): Promise<any> => {
      if (slugSegments.length < 2) return null;

      const topicSlug = slugSegments[slugSegments.length - 1];
      const categorySegments = slugSegments.slice(0, -1);

      // Single query: topic + minimal category ancestry needed for breadcrumbs.
      const { data: topics, error: topicError } = await supabase
        .from('topics')
        .select(`
          id, title, slug, content, created_at, updated_at, user_id, category_id,
          view_count, reply_count, last_post_at,
          is_pinned, is_locked,
          categories:category_id(
            id, name, slug, color, parent_category_id,
            parent_category:parent_category_id(
              id, name, slug, parent_category_id,
              parent_category:parent_category_id(id, name, slug)
            )
          )
        `)
        .eq('slug', topicSlug)
        .limit(5);

      if (topicError) throw topicError;
      if (!topics || topics.length === 0) throw new Error('Topic not found');

      // Pick the topic whose category ancestry matches the URL segments.
      const matchSlugs = (c: any): string[] => {
        const out: string[] = [];
        let node: any = c;
        while (node) {
          out.unshift(node.slug);
          node = node.parent_category;
        }
        return out;
      };
      const topic =
        topics.find((t: any) => {
          const ancestry = matchSlugs(t.categories);
          // Match the trailing N segments
          return categorySegments.every(
            (seg, i) => ancestry[ancestry.length - categorySegments.length + i] === seg,
          );
        }) || topics[0];

      // Use topic.reply_count instead of a separate count query.
      return { ...topic, profiles: null, postsCount: topic.reply_count ?? 0 };
    },
    enabled: slugSegments.length >= 2,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
};
